import React from 'react';
import { BiBot } from 'react-icons/bi';
import { Github, Twitter, Linkedin, Youtube } from 'lucide-react';

export const Footer: React.FC = () => {
  const footerSections = {
    product: {
      title: 'Product',
      links: [
        { label: 'Features', href: '#features' },
        { label: 'Pricing', href: '#pricing' },
        { label: 'Use Cases', href: '#use-cases' },
        { label: 'Demo', href: '#demo' },
        { label: 'Integrations', href: '/integrations' },
      ],
    },
    company: {
      title: 'Company',
      links: [
        { label: 'About Us', href: '/about' },
        { label: 'Blog', href: '/blog' },
        { label: 'Careers', href: '/careers' },
        { label: 'Contact', href: '/contact' },
      ],
    },
    resources: {
      title: 'Resources',
      links: [
        { label: 'Documentation', href: '/docs' },
        { label: 'API Reference', href: '/api-docs' },
        { label: 'Help Center', href: '/help' },
        { label: 'Community', href: '/community' },
      ],
    },
    legal: {
      title: 'Legal',
      links: [
        { label: 'Terms of Service', href: '/terms' },
        { label: 'Privacy Policy', href: '/privacy' },
        { label: 'Security', href: '/security' },
        { label: 'GDPR', href: '/gdpr' },
      ],
    },
  };

  const socialLinks = [
    { icon: Twitter, href: 'https://twitter.com/loopletai', label: 'Twitter' },
    { icon: Github, href: 'https://github.com/loopletai', label: 'GitHub' },
    { icon: Linkedin, href: 'https://linkedin.com/company/loopletai', label: 'LinkedIn' },
    { icon: Youtube, href: 'https://youtube.com/@loopletai', label: 'YouTube' },
  ];

  return (
    <footer className="relative border-t border-gray-800 bg-gray-900">
      <div className="container mx-auto px-6 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 mb-12">
          {/* Brand Section */}
          <div className="col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <BiBot size={32} className="text-purple-400" />
              <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Looplet AI
              </span>
            </div>
            <p className="text-gray-400 mb-6 max-w-sm">
              Build intelligent, multi-channel AI assistants in minutes with our no-code platform.
            </p>
            {/* Social Links */}
            <div className="flex gap-4">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                    aria-label={social.label}
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Footer Links */}
          {Object.entries(footerSections).map(([key, section]) => (
            <div key={key}>
              <h3 className="text-white font-semibold mb-4">{section.title}</h3>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-sm">
            © 2025 Looplet AI. All rights reserved.
          </p>
          <p className="text-gray-500 text-sm">
            Built with ❤️ for teams who care about their customers
          </p>
        </div>
      </div>
    </footer>
  );
};
